import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => article.relatedArticles.includes(a.slug))
            .slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center py-16">
          <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </span>
          <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-600 mb-6">该文章可能已被移除或链接有误</p>
          <Link
            to="/"
            className="inline-flex px-5 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const schemaData = {
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '日本区域指南',
    },
    image: article.image,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://japan-zone-guide.readdy.ai/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://japan-zone-guide.readdy.ai/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://japan-zone-guide.readdy.ai/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SchemaOrg type="Article" data={schemaData} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <article className="min-h-screen bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto pt-6 pb-14">
          <Breadcrumb items={breadcrumbItems} />

          {/* Header */}
          <header className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="text-xs text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full hover:bg-primary-100 transition-colors duration-200 whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-2 mt-4 text-sm text-foreground-500">
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-user-line text-xs"></i>
              </span>
              <span>{article.author}</span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="relative w-full h-56 md:h-80 rounded-lg overflow-hidden mb-8 md:mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="prose-custom">
            {article.content.map((paragraph, i) => (
              <p
                key={i}
                className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5"
              >
                {paragraph}
              </p>
            ))}
          </div>

          {/* Tags */}
          <div className="flex gap-2 mt-8 pt-6 border-t border-background-200/70 flex-wrap">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-secondary-700 bg-secondary-100 px-3 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/70">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-5">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-300 overflow-hidden"
                  >
                    <div className="relative w-full h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                        {ra.categoryName}
                      </span>
                      <h3 className="font-heading font-medium text-sm text-foreground-950 mt-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}